import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  updateProfile,
  signInAnonymously,
  linkWithPopup,
  linkWithCredential,
  signInWithCredential,
  GoogleAuthProvider,
} from 'firebase/auth';
import { auth, googleProvider, signInWithPopup } from './firebase';
import { doc, setDoc, updateDoc, getDoc, arrayUnion, writeBatch, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';

/**
 * Sign up with email and password
 */
export const signUpWithEmail = async (email, password, displayName = null) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);

    // Update display name if provided
    if (displayName && userCredential.user) {
      await updateProfile(userCredential.user, {
        displayName: displayName
      });
    }

    // Create user document in Firestore
    await setDoc(doc(db, 'users', userCredential.user.uid), {
      email: userCredential.user.email,
      displayName: displayName || null,
      photoURL: null,
      createdAt: serverTimestamp(),
      lastLoginAt: serverTimestamp(),
      circleIds: [],
      isAnonymous: false,
    });

    return {
      user: userCredential.user,
      error: null
    };
  } catch (error) {
    console.error('Error signing up:', error);
    return {
      user: null,
      error: error.message
    };
  }
};

/**
 * Sign in with email and password
 */
export const signInWithEmail = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    
    // Update last login
    await setDoc(doc(db, 'users', userCredential.user.uid), {
      lastLoginAt: serverTimestamp(),
    }, { merge: true });

    return {
      user: userCredential.user,
      error: null
    };
  } catch (error) {
    console.error('Error signing in:', error);
    return {
      user: null,
      error: error.message
    };
  }
};

/**
 * Sign in with Google (popup method)
 */
export const signInWithGooglePopup = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    
    // Update/create user document
    await setDoc(doc(db, 'users', result.user.uid), {
      email: result.user.email,
      displayName: result.user.displayName || null,
      photoURL: result.user.photoURL || null,
      lastLoginAt: serverTimestamp(),
      isAnonymous: false,
    }, { merge: true });

    return {
      user: result.user,
      error: null
    };
  } catch (error) {
    console.error('Error signing in with Google:', error);
    return {
      user: null,
      error: error.message
    };
  }
};

/**
 * Send password reset email
 */
export const resetPasswordEmail = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return {
      error: null
    };
  } catch (error) {
    console.error('Error sending password reset:', error);
    return {
      error: error.message
    };
  }
};

/**
 * Send sign-in link to email (passwordless)
 */
export const sendEmailSignInLink = async (email) => {
  // Use the Art Ping Pong URL, not the current origin
  const hostname = window.location.hostname;
  let artPingPongUrl;
  
  if (hostname === 'localhost') {
    artPingPongUrl = 'http://localhost:3000';
  } else if (hostname.includes('artpingpong')) {
    artPingPongUrl = window.location.origin;
  } else {
    artPingPongUrl = 'https://artpingpong.com';
  }
    
  const actionCodeSettings = {
    url: artPingPongUrl + '/finishSignIn',
    handleCodeInApp: true,
  };

  try {
    await sendSignInLinkToEmail(auth, email, actionCodeSettings);
    window.localStorage.setItem('emailForSignIn', email);
    return {
      error: null
    };
  } catch (error) {
    console.error('Error sending email link:', error);
    return {
      error: error.message
    };
  }
};

/**
 * Complete sign-in with email link
 */
export const completeEmailSignIn = async (emailLink) => {
  if (!isSignInWithEmailLink(auth, emailLink)) {
    return {
      user: null,
      error: 'Invalid sign-in link'
    };
  }

  let email = window.localStorage.getItem('emailForSignIn');
  if (!email) {
    email = window.prompt('Please provide your email for confirmation');
  }

  try {
    const result = await signInWithEmailLink(auth, email, emailLink);
    window.localStorage.removeItem('emailForSignIn');

    await setDoc(doc(db, 'users', result.user.uid), {
      email: result.user.email,
      displayName: result.user.displayName || null,
      photoURL: result.user.photoURL || null,
      lastLoginAt: serverTimestamp(),
      isAnonymous: false,
    }, { merge: true });

    return {
      user: result.user,
      error: null
    };
  } catch (error) {
    console.error('Error completing email link sign-in:', error);
    return {
      user: null,
      error: error.message
    };
  }
};

/**
 * Sign in anonymously
 */
export const signInAsAnonymous = async () => {
  try {
    const result = await signInAnonymously(auth);
    const userRef = doc(db, 'users', result.user.uid);
    await setDoc(userRef, {
      isAnonymous: true,
      displayName: 'Guest Artist',
      circleIds: [],
      createdAt: serverTimestamp(),
      lastLoginAt: serverTimestamp(),
    }, { merge: true });
    window.localStorage.setItem('anonymousUid', result.user.uid);
    return {
      user: result.user,
      error: null
    };
  } catch (error) {
    console.error('Error signing in anonymously:', error);
    return {
      user: null,
      error: error.message
    };
  }
};

/**
 * Link anonymous account to email/password credentials
 */
export const linkAnonymousToEmailPassword = async (email, password, displayName = null) => {
  try {
    const { EmailAuthProvider, linkWithCredential } = await import('firebase/auth');
    const credential = EmailAuthProvider.credential(email, password);
    const result = await linkWithCredential(auth.currentUser, credential);
    
    // Update display name if provided
    if (displayName) {
      await updateProfile(result.user, { displayName });
    }
    
    // Update user document
    const userRef = doc(db, 'users', result.user.uid);
    await updateDoc(userRef, {
      isAnonymous: false,
      email: result.user.email,
      displayName: displayName || result.user.displayName || null,
      lastLoginAt: serverTimestamp(),
    });
    
    window.localStorage.removeItem('anonymousUid');
    return {
      user: result.user,
      error: null
    };
  } catch (error) {
    console.error('Error linking anonymous account to email:', error);
    
    if (error.code === 'auth/email-already-in-use' || error.code === 'auth/credential-already-in-use') {
      return {
        user: null,
        error: 'This email is already in use. Please sign in with your existing account instead.',
        errorCode: 'email-already-in-use'
      };
    }
    
    return {
      user: null,
      error: error.message
    };
  }
};

/**
 * Link anonymous account to Google.
 * If the Google account already exists, migrate circle data to the existing account.
 */
export const linkAnonymousToGoogle = async () => {
  const anonymousUser = auth.currentUser;

  try {
    const provider = new GoogleAuthProvider();
    const result = await linkWithPopup(anonymousUser, provider);
    const userRef = doc(db, 'users', result.user.uid);
    await updateDoc(userRef, {
      isAnonymous: false,
      email: result.user.email,
      displayName: result.user.displayName,
      photoURL: result.user.photoURL,
      lastLoginAt: serverTimestamp(),
    });
    window.localStorage.removeItem('anonymousUid');
    return { user: result.user, error: null };
  } catch (error) {
    console.error('Error linking anonymous account:', error);

    // Google account already exists — try to migrate data by signing in with Google directly
    if (error.code === 'auth/credential-already-in-use') {
      try {
        console.log('🔄 Credential already in use, attempting migration...');
        
        // Read anonymous user's circles before they're replaced
        const anonUserSnap = await getDoc(doc(db, 'users', anonymousUser.uid));
        const anonCircleIds = anonUserSnap.exists() ? (anonUserSnap.data().circleIds || []) : [];
        console.log('📋 Anonymous user circles:', anonCircleIds);

        // Sign in with Google using popup (this will replace the anonymous user)
        const provider = new GoogleAuthProvider();
        const existingResult = await signInWithPopup(auth, provider);
        const existingUid = existingResult.user.uid;
        console.log('✅ Signed in with existing account:', existingUid);

        if (anonCircleIds.length > 0) {
          console.log('🔄 Migrating circle data...');
          const batch = writeBatch(db);

          // Merge circles into existing user doc
          batch.set(doc(db, 'users', existingUid), {
            circleIds: arrayUnion(...anonCircleIds),
            isAnonymous: false,
            lastLoginAt: serverTimestamp(),
          }, { merge: true });

          // Swap anonymous UID for real UID in each circle
          for (const circleId of anonCircleIds) {
            const circleRef = doc(db, 'artPingPongCircles', circleId);
            const circleSnap = await getDoc(circleRef);
            if (!circleSnap.exists()) continue;

            const circleData = circleSnap.data();
            const updatedMemberIds = (circleData.memberIds || []).map(
              id => id === anonymousUser.uid ? existingUid : id
            );
            const updatedMemberInfo = { ...(circleData.memberInfo || {}) };
            if (updatedMemberInfo[anonymousUser.uid]) {
              updatedMemberInfo[existingUid] = {
                ...updatedMemberInfo[anonymousUser.uid],
                displayName: existingResult.user.displayName || updatedMemberInfo[anonymousUser.uid].displayName,
                photoURL: existingResult.user.photoURL || null,
              };
              delete updatedMemberInfo[anonymousUser.uid];
            }
            batch.update(circleRef, { memberIds: updatedMemberIds, memberInfo: updatedMemberInfo });
          }

          await batch.commit();
          console.log('✅ Migration complete');
        }

        window.localStorage.removeItem('anonymousUid');
        return { user: existingResult.user, error: null };
      } catch (migrationError) {
        console.error('Error migrating anonymous data:', migrationError);
        return { user: null, error: "Couldn't complete sign-in. Please try again or use email instead." };
      }
    }

    return { user: null, error: error.message };
  }
};
